import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const CreatePost = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'BTC'
  });
  const [submitting, setSubmitting] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const categories = ['BTC', 'Altcoins', 'Stocks', 'Futures', 'Long-Term Investing', 'Entrepreneurship', 'Economics'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/forum/posts`,
        formData,
        { withCredentials: true }
      );
      toast.success('Post created successfully');
      navigate(`/forum/${response.data.post_id}`);
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/forum" className="inline-flex items-center text-gray-400 hover:text-[#C5A059] transition-colors mb-8" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" />
            Back to Forum
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-manrope font-bold text-4xl text-white mb-2" data-testid="create-post-title">
              Create New Post
            </h1>
            <p className="text-gray-400">
              Share your insights with the community
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-[#111111] border border-white/10 rounded-lg p-8">
            <div className="space-y-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Category
                </label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white rounded-sm h-12" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-white/10 text-white">
                    {categories.map((category) => (
                      <SelectItem key={category} value={category} className="hover:bg-white/5 cursor-pointer">
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Title
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="What's on your mind?"
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] rounded-sm h-12"
                  data-testid="title-input"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Content
                </label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Share your thoughts, analysis, or questions..."
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] rounded-sm min-h-[200px]"
                  data-testid="content-input"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-8 py-3 font-semibold shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)] transition-all"
                  data-testid="submit-button"
                >
                  {submitting ? 'Publishing...' : 'Publish Post'}
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate('/forum')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 rounded-sm px-8 py-3"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreatePost;
